import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../../base/icons/components/Icon';
import { IconTools } from '../../../base/icons/svg';
import { isCompactCallBar } from '../../../toolbox/clientCallActions.web';
import { toggleInterpreterTools } from '../../actions.web';
import { INTERPRETER_TOOLS_PANEL_ID } from '../../constants';
import { toolsLiveState } from '../../sections';
import usePhoneLine from '../../usePhoneLine';
const useStyles = makeStyles()(theme => {
    return {
        key: {
            background: 'transparent',
            border: 0,
            color: theme.palette.text01,
            cursor: 'pointer',
            display: 'inline-flex',
            margin: '0 4px',
            padding: 0,
            '&:focus-visible': {
                borderRadius: 8,
                outline: `2px solid ${theme.palette.focus01}`,
                outlineOffset: 2
            }
        },
        // Rides on the bar's .toolbox-icon (48 px, rounded, lit on hover); a
        // label or live state widens it.
        face: {
            alignItems: 'center',
            boxSizing: 'border-box',
            flexDirection: 'row',
            gap: 8,
            minWidth: 48,
            padding: '0 12px',
            position: 'relative',
            width: 'auto'
        },
        compactFace: {
            padding: 0,
            width: 48
        },
        label: {
            fontSize: 14,
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 600,
            lineHeight: '20px',
            whiteSpace: 'nowrap'
        },
        dot: {
            borderRadius: '50%',
            flexShrink: 0,
            height: 8,
            width: 8
        },
        compactDot: {
            position: 'absolute',
            right: 8,
            top: 8
        },
        phoneDot: { background: '#4ADE80' },
        waitingDot: { background: '#FBBF24' },
        alertDot: { background: '#F87171' }
    };
});
/**
 * The words for the key's live state, or '' when all is quiet.
 *
 * @param {IToolsLiveState} live - The live state.
 * @param {Function} t - Translates a key.
 * @returns {string}
 */
export function liveStateText(live, t) {
    const phone = live.phone === 'connected'
        ? live.timer || t('vrs.tools.connected')
        : live.phone ? t(`vrs.tools.${live.phone}`) : '';
    return [phone, live.transferPending ? t('vrs.tools.transferPending') : ''].filter(Boolean).join(' · ');
}
/**
 * The interpreter's Tools key on the call bar. It opens the docked Tools
 * panel and takes it back; minimized, it shows only live state: a phone leg
 * with its timer, a transfer waiting for acceptance. It shows whenever the
 * room shows the panel (the panel owns that decision).
 *
 * @returns {ReactElement | null}
 */
export default function InterpreterToolsButton() {
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    const tools = useSelector((state) => state['features/interpreter-tools']);
    const compact = useSelector(isCompactCallBar);
    const phone = usePhoneLine();
    const live = toolsLiveState(phone.status, phone.duration, Boolean(tools?.transferPending));
    const liveText = liveStateText(live, t);
    const onClick = useCallback(() => dispatch(toggleInterpreterTools()), [dispatch]);
    if (!tools?.mounted) {
        return null;
    }
    const dot = live.phone === 'endUnconfirmed' ? classes.alertDot
        : live.phone === 'connected' ? classes.phoneDot
            : live.phone || live.transferPending ? classes.waitingDot : undefined;
    const title = t('toolbar.accessibilityLabel.interpreterTools');
    return (React.createElement("button", { "aria-controls": INTERPRETER_TOOLS_PANEL_ID, "aria-expanded": tools.open, "aria-label": liveText ? `${title}, ${liveText}` : title, className: classes.key, "data-interpreter-tools-key": 'true', onClick: onClick, title: liveText || title, type: 'button' },
        React.createElement("div", { className: cx('toolbox-icon', tools.open && 'toggled', classes.face, compact && classes.compactFace) },
            React.createElement(Icon, { size: 24, src: IconTools }),
            dot && React.createElement("span", { "aria-hidden": 'true', className: cx(classes.dot, dot, compact && classes.compactDot) }),
            !compact && React.createElement("span", { className: classes.label }, liveText || t('toolbar.interpreterTools')))));
}
